import React, { useState } from 'react';
import { Calendar, X } from 'lucide-react';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
}

type PresetPeriod = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateRangeChange
}) => {
  const [selectedPreset, setSelectedPreset] = useState<PresetPeriod>('thisMonth');
  const [showCustom, setShowCustom] = useState(false);

  const getPresetDates = (preset: PresetPeriod): { start: string; end: string } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (preset) {
      case 'today':
        return {
          start: today.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };

      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          start: yesterday.toISOString().split('T')[0],
          end: yesterday.toISOString().split('T')[0]
        };
      }

      case 'thisWeek': {
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay());
        return {
          start: firstDayOfWeek.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      }

      case 'lastWeek': {
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        return {
          start: lastWeekStart.toISOString().split('T')[0],
          end: lastWeekEnd.toISOString().split('T')[0]
        };
      }

      case 'thisMonth': {
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          start: firstDayOfMonth.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      }

      case 'lastMonth': {
        const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          start: firstDayOfLastMonth.toISOString().split('T')[0],
          end: lastDayOfLastMonth.toISOString().split('T')[0]
        };
      }

      default:
        return { start: startDate, end: endDate };
    }
  };

  const handlePresetChange = (preset: PresetPeriod) => {
    setSelectedPreset(preset);
    if (preset === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      const dates = getPresetDates(preset);
      onDateRangeChange(dates.start, dates.end);
    }
  };

  const handleCustomDateChange = (type: 'start' | 'end', value: string) => {
    if (type === 'start') {
      onDateRangeChange(value, endDate);
    } else {
      onDateRangeChange(startDate, value);
    }
  };

  const presets = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'lastWeek', label: 'Last Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-white mb-2">
        <Calendar size={16} />
        Date Range
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {presets.map((preset) => (
          <button
            key={preset.value}
            onClick={() => handlePresetChange(preset.value as PresetPeriod)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedPreset === preset.value
                ? 'bg-kod-accent text-white'
                : 'bg-kod-purple-700 text-white/80 hover:bg-kod-purple-600'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {showCustom && (
        <div className="bg-kod-purple-700 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Custom Date Range</span>
            <button
              onClick={() => {
                setShowCustom(false);
                setSelectedPreset('thisMonth');
                const dates = getPresetDates('thisMonth');
                onDateRangeChange(dates.start, dates.end);
              }}
              className="text-white/60 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-white/70 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleCustomDateChange('start', e.target.value)}
                className="w-full px-3 py-2 bg-kod-purple-800 border border-kod-purple-600 rounded-lg text-white text-sm focus:outline-none focus:border-kod-accent"
              />
            </div>

            <div>
              <label className="block text-xs text-white/70 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleCustomDateChange('end', e.target.value)}
                className="w-full px-3 py-2 bg-kod-purple-800 border border-kod-purple-600 rounded-lg text-white text-sm focus:outline-none focus:border-kod-accent"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
